import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '@/components/feedback/EmptyState';
import { ErrorState } from '@/components/feedback/ErrorState';
import { SkeletonList } from '@/components/feedback/Skeleton';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui';
import type { NomenclatureResolver } from '@/lib/nomenclature/resolver';
import { useActivePatient } from '@/features/patients/hooks/useActivePatient';
import { useResolver } from '@/features/nomenclature/hooks/useResolver';
import { useResults } from '../hooks/useResults';
import { ResultCard } from '../components/ResultCard';
import { groupResultsByCategory, type GroupedResults } from '../lib/categories';

/** How many cards to show per category before the "see all" toggle. */
const PREVIEW_COUNT = 2;

export function ResultsPage() {
  const { t } = useTranslation();
  const { patient } = useActivePatient();
  const { data: results = [], isLoading, isError, refetch } = useResults(patient?.fhirId ?? null);
  const { data: resolver = null } = useResolver();

  const groups = useMemo(() => groupResultsByCategory(results), [results]);

  return (
    <div className="flex flex-col gap-5">
      <PageHeader title={t('results.title')} subtitle={t('results.subtitle')} />

      {isLoading ? (
        <SkeletonList rows={4} />
      ) : isError ? (
        <ErrorState description={t('results.load_error')} onRetry={() => void refetch()} />
      ) : results.length === 0 ? (
        <EmptyState title={t('results.empty')} description={t('results.empty_hint')} />
      ) : (
        <div className="flex flex-col gap-6">
          {groups.map((group) => (
            <CategorySection key={group.category.key} group={group} resolver={resolver} />
          ))}
        </div>
      )}
    </div>
  );
}

interface CategorySectionProps {
  group: GroupedResults;
  resolver: NomenclatureResolver | null;
}

/** One category: shows the first few results, with a "see all" toggle for the rest. */
function CategorySection({ group, resolver }: CategorySectionProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const { category, results } = group;

  const hasMore = results.length > PREVIEW_COUNT;
  const visible = expanded ? results : results.slice(0, PREVIEW_COUNT);

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <h2 className="text-base font-bold text-ink">{category.name}</h2>
        <Badge tone="neutral">{results.length}</Badge>
      </div>
      <div className="flex flex-col gap-3">
        {visible.map((result, index) => (
          <ResultCard key={`${result.consId}-${index}`} result={result} resolver={resolver} />
        ))}
      </div>
      {hasMore && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="self-start text-sm font-semibold text-primary hover:underline"
        >
          {expanded ? t('results.see_less') : t('results.see_all', { count: results.length })}
        </button>
      )}
    </section>
  );
}
